/**
 * Reviews hub — renders assets/yotpo-reviews.json into the section's markup.
 *
 * WHY CLIENT-SIDE: the asset is ~1MB (about 240KB gzipped) and holds 2,700+
 * reviews. Inlining that through Liquid would put the whole payload in the
 * document for the sake of the ten rows actually on screen. Fetching it after
 * paint keeps the HTML small and lets filtering, sorting and pagination happen
 * without a round trip.
 *
 * The markup this builds mirrors the placeholder in sections/reviews-hub.liquid
 * one-for-one, so assets/section-reviews-hub.css styles both. Change a class
 * here and change it there.
 *
 * FILTERS come from Shopify collections, not from Yotpo — see the export
 * script's header. Each group is a collection handle list configured in the
 * theme editor; a review belongs to a group if its product_id is in that
 * collection's product_ids.
 */
(function () {
  'use strict';

  // Three per page, matching the design's three-row list (2036:5556).
  var PAGE_SIZE = 3;

  /** Escape text before it goes near innerHTML. Review content is user-written. */
  function esc(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * "12 JUNE 2026" — the full review date.
   *
   * The design stamps these as month and year only ("JUNE 2026"), but a review
   * carries more weight when the reader can see exactly when it was left, and
   * a month alone reads as vaguer than the data actually is.
   *
   * `en-AU` so the day leads the month, matching the rest of the store.
   */
  function formatDate(iso) {
    if (!iso) return '';
    var date = new Date(iso);
    if (isNaN(date)) return '';
    return date
      .toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
      .toUpperCase();
  }

  function Hub(root) {
    this.root = root;
    this.src = root.getAttribute('data-reviews-src');
    this.data = null;
    this.state = { filters: {}, sort: 'recent', page: 1 };
    // True while the rating filter was set by clicking a distribution row.
    this.ratingFromRow = false;

    // Scope to one product when the section is used on a PDP. Absent on the
    // reviews page, where every review belongs. Compared as a string because
    // the export stores product_id as one.
    this.productId = root.getAttribute('data-reviews-product-id') || '';

    // Groups are emitted by Liquid as [{label, handles:[…]}, …].
    try {
      this.groups = JSON.parse(root.getAttribute('data-filter-groups') || '[]');
    } catch (e) {
      this.groups = [];
    }
  }

  Hub.prototype.init = function () {
    var self = this;
    if (!this.src) return;

    fetch(this.src)
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(function (data) {
        self.data = data;
        self.root.classList.remove('wt-reviews-hub--loading');
        self.renderSummary();
        self.renderControls();
        self.render();
      })
      .catch(function () {
        // Leave whatever the server rendered in place. A reviews page with no
        // reviews is a poor result, but a broken one is worse — and the
        // placeholder already reads as a placeholder.
        self.root.classList.remove('wt-reviews-hub--loading');
        self.root.classList.add('wt-reviews-hub--failed');
      });
  };

  /** Product ids in a collection, for filter matching. */
  Hub.prototype.idsFor = function (handle) {
    var collection = (this.data.collections || {})[handle];
    return collection ? collection.product_ids : [];
  };

  /** Reviews surviving the active filters. `skipKey` leaves one filter out. */
  Hub.prototype.filtered = function (skipKey) {
    var self = this;
    var reviews = this.data.reviews || [];
    var state = this.state.filters;

    // Product scope is applied before the UI filters and is not user-clearable:
    // on a PDP the other 2,700 reviews are not "unfiltered", they are the wrong
    // product. Done here rather than in the fetch so the summary figures, the
    // filter counts and the pagination all agree on the same subset.
    if (this.productId) {
      reviews = reviews.filter(function (r) {
        return String(r.product_id) === self.productId;
      });
    }

    Object.keys(state).forEach(function (key) {
      var value = state[key];
      if (!value || key === skipKey) return;

      if (key === 'rating') {
        reviews = reviews.filter(function (r) {
          return String(r.score) === value;
        });
        return;
      }

      if (key === 'media') {
        reviews = reviews.filter(function (r) {
          return r.images && r.images.length;
        });
        return;
      }

      // Everything else is a collection handle.
      var ids = self.idsFor(value);
      reviews = reviews.filter(function (r) {
        return ids.indexOf(r.product_id) !== -1;
      });
    });

    if (this.state.sort === 'highest') {
      reviews = reviews.slice().sort(function (a, b) {
        return b.score - a.score;
      });
    } else if (this.state.sort === 'lowest') {
      reviews = reviews.slice().sort(function (a, b) {
        return a.score - b.score;
      });
    }
    // 'recent' needs no work: the export writes the asset newest-first.

    return reviews;
  };

  /** Average and distribution — recomputed so they track the active filters. */
  Hub.prototype.renderSummary = function () {
    var reviews = this.filtered();
    var scoreEl = this.root.querySelector('[data-reviews-score]');
    var countEl = this.root.querySelector('[data-reviews-count]');

    var total = reviews.length;
    var sum = reviews.reduce(function (acc, r) {
      return acc + (r.score || 0);
    }, 0);
    var average = total ? Math.round((sum / total) * 10) / 10 : 0;

    if (scoreEl) scoreEl.textContent = average ? average.toFixed(1) : '—';
    if (countEl) countEl.textContent = total.toLocaleString('en-AU');

    // The distribution ignores the rating filter. It is also the control for
    // that filter, so narrowing to 5 stars must not collapse the other bars to
    // nothing and leave no way to see what the other ratings hold.
    var counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    this.filtered('rating').forEach(function (r) {
      if (counts[r.score] !== undefined) counts[r.score] += 1;
    });

    // Bars are relative to the largest bucket, not to the total: at a 4.77
    // average the 5-star row holds ~85% of reviews, so scaling by total would
    // flatten every other row to a sliver.
    var peak = Math.max(counts[1], counts[2], counts[3], counts[4], counts[5], 1);
    for (var star = 1; star <= 5; star += 1) {
      var fill = this.root.querySelector('[data-dist-fill="' + star + '"]');
      if (fill) fill.style.width = (counts[star] / peak) * 100 + '%';
    }
  };

  /** Filter pills and the sort control. */
  Hub.prototype.renderControls = function () {
    var self = this;
    var wrap = this.root.querySelector('[data-reviews-filters]');
    if (!wrap) return;

    var html = '';

    // Rating is native to the review, not a collection.
    html +=
      '<label class="wt-reviews-hub__filter">' +
      '<select data-filter-key="rating">' +
      '<option value="">Rating</option>';
    for (var star = 5; star >= 1; star -= 1) {
      html +=
        '<option value="' + star + '">' + star + ' star' +
        (star > 1 ? 's' : '') + '</option>';
    }
    html += '</select></label>';

    // Collection-backed groups, in the order the theme editor lists them.
    this.groups.forEach(function (group, index) {
      if (!group.handles || !group.handles.length) return;

      var options = group.handles
        .map(function (handle) {
          var collection = (self.data.collections || {})[handle];
          if (!collection) return '';
          return (
            '<option value="' + esc(handle) + '">' +
            esc(collection.title) +
            '</option>'
          );
        })
        .join('');

      // A group whose collections are all missing from the asset would render
      // an empty dropdown, so skip it rather than show a dead control.
      if (!options) return;

      html +=
        '<label class="wt-reviews-hub__filter">' +
        '<select data-filter-key="group' + index + '">' +
        '<option value="">' + esc(group.label) + '</option>' +
        options +
        '</select></label>';
    });

    html +=
      '<label class="wt-reviews-hub__filter wt-reviews-hub__filter--toggle">' +
      '<input type="checkbox" data-filter-key="media"> With media' +
      '</label>';

    wrap.innerHTML = html;

    // Distribution rows are a second way into the rating filter: clicking one
    // selects that rating, clicking the selected one again clears it. Both
    // routes go through setRating so the dropdown and rows stay in step.
    this.root.addEventListener('click', function (event) {
      var row = event.target.closest('[data-dist-rating]');
      if (!row) return;
      var rating = row.getAttribute('data-dist-rating');
      var next = self.state.filters.rating === rating ? '' : rating;
      self.setRating(next);
      self.ratingFromRow = !!next;
    });

    // A rating picked from a row is a quick look, so clicking away drops it.
    // Clicks on the list, pagination and filter controls don't count as away:
    // paging through the 5-star reviews must not throw the filter out. A
    // rating chosen from the dropdown is a deliberate filter and stays put.
    var keep = '[data-dist-rating], [data-reviews-list], [data-reviews-pagination], ' +
      '[data-reviews-filters], [data-reviews-sort]';
    document.addEventListener('click', function (event) {
      if (!self.ratingFromRow) return;
      // A page button is replaced as the list re-renders, so by the time the
      // click reaches the document its target may be detached and no longer
      // inside anything. Treat that as a click inside, not away.
      if (!document.contains(event.target)) return;
      // The photo lightbox opens outside the section; closing it isn't "away".
      if (event.target.closest(keep + ', .pswp')) return;
      self.ratingFromRow = false;
      self.setRating('');
    });

    wrap.addEventListener('change', function (event) {
      var key = event.target.getAttribute('data-filter-key');
      if (!key) return;
      self.state.filters[key] =
        event.target.type === 'checkbox'
          ? event.target.checked
            ? '1'
            : ''
          : event.target.value;
      if (key === 'rating') self.ratingFromRow = false;
      self.state.page = 1;
      self.syncRatingRows();
      self.renderSummary();
      self.render();
    });

    var sort = this.root.querySelector('[data-reviews-sort]');
    if (sort) {
      sort.addEventListener('change', function () {
        self.state.sort = sort.value;
        self.state.page = 1;
        self.render();
      });
    }
  };

  /** Select a rating ('' clears it) from either the dropdown or a row. */
  Hub.prototype.setRating = function (rating) {
    this.state.filters.rating = rating;
    var select = this.root.querySelector('[data-filter-key="rating"]');
    if (select) select.value = rating;
    this.state.page = 1;
    this.syncRatingRows();
    this.renderSummary();
    this.render();
  };

  /** Mark the selected distribution row, and flag the group as filtered. */
  Hub.prototype.syncRatingRows = function () {
    var rating = this.state.filters.rating || '';
    var rows = this.root.querySelectorAll('[data-dist-rating]');
    Array.prototype.forEach.call(rows, function (row) {
      row.setAttribute(
        'aria-pressed',
        row.getAttribute('data-dist-rating') === rating ? 'true' : 'false'
      );
    });
    var dist = this.root.querySelector('.wt-reviews-hub__distribution');
    if (dist) dist.classList.toggle('wt-reviews-hub__distribution--filtered', !!rating);
  };

  Hub.prototype.render = function () {
    var list = this.root.querySelector('[data-reviews-list]');
    if (!list) return;

    var reviews = this.filtered();
    var start = (this.state.page - 1) * PAGE_SIZE;
    var slice = reviews.slice(start, start + PAGE_SIZE);

    if (!slice.length) {
      list.innerHTML =
        '<p class="wt-reviews-hub__empty">No reviews match those filters.</p>';
      this.renderPagination(0);
      return;
    }

    list.innerHTML = slice.map(this.reviewHtml, this).join('');
    this.renderPagination(reviews.length);
  };

  Hub.prototype.reviewHtml = function (review) {
    // The design shows a score out of 10; Yotpo stores out of 5.
    var outOfTen = review.score * 2;
    var percent = (review.score / 5) * 100;

    // The reviewed product: name for the pill beside the score, primary photo
    // for the thumbnail under the reviewer. A review carries only product_id,
    // so both come from the export's `products` map.
    //
    // `product_titles` is the older name-only shape — read as a fallback so a
    // cached or not-yet-regenerated asset still renders the pill. Either way a
    // missing product yields an empty string and the element is omitted rather
    // than rendered blank, which would read as a fault.
    var catalogue = (this.data && this.data.products) || {};
    var entry = catalogue[review.product_id] || {};
    var legacyTitles = (this.data && this.data.product_titles) || {};
    var productName = entry.title || legacyTitles[review.product_id] || '';
    var productImage = entry.image || '';
    // Both the pill and the photo link to the product when the export captured
    // a handle. Built from the handle rather than a stored URL so it stays
    // correct on any domain the theme is served from.
    var productUrl = entry.handle ? '/products/' + entry.handle : '';

    // Review photos, wrapped in the anchors PhotoSwipe expects — the same
    // markup contract the product gallery uses (sections/main-product.liquid:
    // an <a href> to the full image inside a `.photoswipe` container).
    //
    // NO data-pswp-width/height: Yotpo's export carries no dimensions, and the
    // library falls back to 0, which cannot size a slide. They are filled in
    // from the loaded thumbnail on first click instead — see openMediaViewer.
    var media = (review.images || [])
      .slice(0, 3)
      .map(function (src) {
        return (
          '<a class="wt-reviews-hub__review-media-link" href="' +
          esc(src) +
          '">' +
          '<img class="wt-reviews-hub__review-image" src="' +
          esc(src) +
          '" alt="" loading="lazy" width="80" height="80">' +
          '</a>'
        );
      })
      .join('');

    return (
      '<article class="wt-reviews-hub__review">' +
      '<header class="wt-reviews-hub__review-author">' +
      '<p class="wt-reviews-hub__review-name">' + esc(review.name) + '</p>' +
      (review.verified
        ? '<p class="wt-reviews-hub__review-verified">Verified reviewer</p>'
        : '') +
      // The reviewed product's primary photo, under the reviewer.
      //
      // When it links, the <a> needs an accessible name, and an empty alt on
      // the only child would leave the link unlabelled — so the alt carries
      // the product name there, and stays empty when the image is inert and
      // the pill beside the score already names the product.
      (productImage
        ? (productUrl
            ? '<a class="wt-reviews-hub__review-product-link" href="' +
              esc(productUrl) +
              '"><img class="wt-reviews-hub__review-product-image" src="' +
              esc(productImage) +
              '" alt="' +
              esc(productName) +
              '" loading="lazy" width="80" height="80"></a>'
            : '<img class="wt-reviews-hub__review-product-image" src="' +
              esc(productImage) +
              '" alt="" loading="lazy" width="80" height="80">')
        : '') +
      '</header>' +
      '<div class="wt-reviews-hub__review-body">' +
      '<div class="wt-reviews-hub__review-meta">' +
      '<span class="wt-reviews-hub__review-stars" style="--fill-pct:' +
      percent +
      '%"></span>' +
      '<span class="wt-reviews-hub__review-score">' + outOfTen + '/10</span>' +
      (productName
        ? (productUrl
            ? '<a class="wt-reviews-hub__review-product" href="' +
              esc(productUrl) +
              '">' +
              esc(productName) +
              '</a>'
            : '<span class="wt-reviews-hub__review-product">' +
              esc(productName) +
              '</span>')
        : '') +
      '<span class="wt-reviews-hub__review-date">' +
      esc(formatDate(review.created_at)) +
      '</span>' +
      '</div>' +
      (review.title
        ? '<p class="wt-reviews-hub__review-title">' + esc(review.title) + '</p>'
        : '') +
      // Text and media are siblings in a row (design 2036:5577), so they need
      // a wrapper — without it the strip drops below the text.
      '<div class="wt-reviews-hub__review-content' +
      (media ? ' wt-reviews-hub__review-content--has-media' : '') +
      '">' +
      '<p class="wt-reviews-hub__review-text">' + esc(review.content) + '</p>' +
      // `photoswipe` is the class the lightbox binds galleries to, so each
      // review's strip is its own gallery — opening one shows only that
      // review's photos, not every photo on the page.
      (media
        ? '<div class="wt-reviews-hub__review-media photoswipe">' +
          media +
          '</div>'
        : '') +
      '</div>' +
      '</div>' +
      '</article>'
    );
  };

  Hub.prototype.renderPagination = function (total) {
    var self = this;
    var wrap = this.root.querySelector('[data-reviews-pagination]');
    if (!wrap) return;

    var pages = Math.ceil(total / PAGE_SIZE);
    if (pages <= 1) {
      wrap.innerHTML = '';
      return;
    }

    // A window around the current page: 2,700 reviews is 270 pages, and the
    // design's row has space for a handful.
    var current = this.state.page;
    var from = Math.max(1, current - 2);
    var to = Math.min(pages, from + 4);
    from = Math.max(1, to - 4);

    // 2036:5400 — a bare caret, not the word "Next". aria-label carries the
    // meaning the glyph no longer states.
    var caret =
      '<span class="wt-reviews-hub__pagination-caret" aria-hidden="true"></span>';

    var html = '';
    if (current > 1) {
      html +=
        '<button type="button" class="wt-reviews-hub__pagination-arrow ' +
        'wt-reviews-hub__pagination-arrow--prev" data-page="' +
        (current - 1) + '" aria-label="Previous page">' + caret + '</button>';
    } else {
      // Page 1 has nowhere to go back to, but omitting the control entirely
      // would shunt the numbers left and then jump them right again on page 2.
      // An inert spacer of the same width holds the row still.
      html +=
        '<span class="wt-reviews-hub__pagination-arrow ' +
        'wt-reviews-hub__pagination-spacer" aria-hidden="true"></span>';
    }
    for (var page = from; page <= to; page += 1) {
      html +=
        '<button type="button" data-page="' + page + '"' +
        (page === current ? ' aria-current="page"' : '') +
        '>' + page + '</button>';
    }
    if (current < pages) {
      html +=
        '<button type="button" class="wt-reviews-hub__pagination-arrow" ' +
        'data-page="' + (current + 1) + '" aria-label="Next page">' +
        caret + '</button>';
    } else {
      html +=
        '<span class="wt-reviews-hub__pagination-arrow ' +
        'wt-reviews-hub__pagination-spacer" aria-hidden="true"></span>';
    }
    wrap.innerHTML = html;

    wrap.onclick = function (event) {
      // closest(), not event.target: a click landing on the caret span inside
      // an arrow button would otherwise find no data-page and do nothing.
      var button = event.target.closest('[data-page]');
      if (!button) return;
      var page = button.getAttribute('data-page');
      self.state.page = parseInt(page, 10);
      self.render();
      self.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
  };

  /**
   * Reveal the embedded Yotpo submission widget.
   *
   * Yotpo exposes no API for opening its review form from outside — the loader
   * is minified with nothing on `window` — so the widget is embedded hidden and
   * this uncovers it. Yotpo's own button inside then opens the form.
   *
   * Independent of the Hub: the CTA is page furniture and renders in every
   * review_source mode, not just `data`.
   */
  function bootWriteToggle() {
    document
      .querySelectorAll('[data-reviews-write-toggle]')
      .forEach(function (button) {
        button.addEventListener('click', function () {
          var target = document.getElementById(
            button.getAttribute('aria-controls')
          );
          if (!target) return;

          // The widget must be laid out before Yotpo will render into it or
          // respond to a click, but it is never shown: `hidden` is swapped for
          // an off-screen class so the only thing the shopper sees is Yotpo's
          // own modal, opened directly.
          target.removeAttribute('hidden');
          target.classList.add('wt-reviews-hub__write--offscreen');
          // A staging area, not content — the modal it opens is what should be
          // announced. Removed again in the fallback path below, where the
          // widget does become the thing the shopper interacts with.
          target.setAttribute('aria-hidden', 'true');

          // Yotpo renders lazily and skips nodes that were display:none at
          // load, so it needs a nudge the first time the widget is laid out.
          if (window.yotpo && typeof window.yotpo.refreshWidgets === 'function') {
            window.yotpo.refreshWidgets();
          }

          openYotpoForm(target, button);
        });
      });
  }

  /**
   * Click Yotpo's own "write a review" control inside `scope`.
   *
   * Yotpo exposes no documented API for opening the form — the loader is
   * minified with nothing on `window` — so the only reliable route is to find
   * the button it renders and click it. Which is why this polls: the widget
   * mounts asynchronously and the button does not exist on the first frame.
   *
   * Falls back to simply revealing the widget if the button never appears, so a
   * Yotpo markup change degrades to the old two-step flow rather than a CTA
   * that does nothing.
   */
  function openYotpoForm(scope, button) {
    var SELECTORS = [
      '.write-review-button',
      '.yotpo-review-form-button',
      '[data-testid="write-review-button"]',
      '.yotpo-bottom-line-right-panel .yotpo-icon-write',
      'button.yotpo-new-review-btn'
    ].join(', ');

    var attempts = 0;
    var MAX_ATTEMPTS = 40; // ~4s at 100ms

    (function poll() {
      var trigger = scope.querySelector(SELECTORS);
      if (trigger) {
        trigger.click();
        // Yotpo's modal is its own overlay, so the mounted widget goes back
        // off-screen — it has done its job.
        button.setAttribute('aria-expanded', 'true');
        return;
      }

      if (++attempts >= MAX_ATTEMPTS) {
        // Never found it: show the widget so the shopper can reach Yotpo's own
        // affordance manually rather than being left with a dead button.
        scope.classList.remove('wt-reviews-hub__write--offscreen');
        scope.removeAttribute('aria-hidden');
        scope.scrollIntoView({ behavior: 'smooth', block: 'center' });
        button.setAttribute('aria-expanded', 'true');
        return;
      }

      setTimeout(poll, 100);
    })();
  }

  /**
   * Review media viewer — the same PhotoSwipe overlay the product gallery uses.
   *
   * WHY NOT gallery.js: that module owns the PDP gallery and imports Swiper
   * alongside the lightbox, and layout/theme.liquid only loads it on the
   * product template. Importing the lightbox directly here shares the library
   * without pulling a carousel onto the reviews page.
   *
   * IMPORTED ON FIRST CLICK, not at boot. reviews-hub.js is a classic script,
   * so a static import is not available anyway — and most visitors never open a
   * photo, so the ~40KB stays unfetched until one is opened.
   *
   * DIMENSIONS ARE MEASURED, NOT DECLARED. PhotoSwipe wants width and height
   * up front and falls back to 0 when the anchor has no data-pswp-* pair, which
   * leaves an unsized slide. Yotpo's export has no dimensions, so each anchor
   * is stamped from its own thumbnail's `naturalWidth`/`naturalHeight` before
   * the overlay opens. That is the displayed thumbnail rather than the full
   * image, but the two share an aspect ratio, which is all PhotoSwipe needs to
   * lay the slide out — it then scales to the real file once loaded.
   */
  var lightboxPromise = null;

  function stampDimensions(gallery) {
    gallery
      .querySelectorAll('.wt-reviews-hub__review-media-link')
      .forEach(function (link) {
        if (link.dataset.pswpWidth) return;
        var img = link.querySelector('img');
        // A thumbnail still loading has no natural size yet; a square is a
        // safe assumption for review photos and self-corrects on the next
        // open, once the image is cached.
        var w = (img && img.naturalWidth) || 1200;
        var h = (img && img.naturalHeight) || 1200;
        link.dataset.pswpWidth = String(w);
        link.dataset.pswpHeight = String(h);
      });
  }

  function bootMediaViewer() {
    document.addEventListener(
      'click',
      function (event) {
        var link = event.target.closest('.wt-reviews-hub__review-media-link');
        if (!link) return;
        // Let modified clicks open the image in a new tab as the href implies.
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button) {
          return;
        }
        event.preventDefault();

        var gallery = link.closest('.photoswipe');
        if (!gallery) return;
        stampDimensions(gallery);

        var links = Array.prototype.slice.call(
          gallery.querySelectorAll('.wt-reviews-hub__review-media-link')
        );
        var index = links.indexOf(link);

        // Module URLs come from the section's data attributes — see the note
        // there on why a relative specifier cannot work in a classic script.
        var host = link.closest('[data-pswp-lightbox-src]');
        var lightboxSrc = host && host.getAttribute('data-pswp-lightbox-src');
        var coreSrc = host && host.getAttribute('data-pswp-core-src');
        if (!lightboxSrc || !coreSrc) return;

        if (!lightboxPromise) {
          lightboxPromise = import(lightboxSrc).then(function (mod) {
            var Lightbox = mod.default;
            var instance = new Lightbox({
              gallery: '.wt-reviews-hub__review-media',
              children: '.wt-reviews-hub__review-media-link',
              pswpModule: function () {
                return import(coreSrc);
              },
              bgOpacity: 1,
            });
            instance.init();
            return instance;
          });
        }

        lightboxPromise
          .then(function (instance) {
            instance.loadAndOpen(index < 0 ? 0 : index, { gallery: gallery });
          })
          .catch(function () {
            // Library failed to load — fall back to the href so the photo is
            // still reachable.
            window.open(link.href, '_blank', 'noopener');
          });
      },
      false
    );
  }

  function boot() {
    document.querySelectorAll('[data-reviews-src]').forEach(function (root) {
      new Hub(root).init();
    });
    bootWriteToggle();
    bootMediaViewer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
